<template>
  <div class="create-book-modal">
    <!--Step 1: 书籍信息-->
    <template v-if="step === 1">
      <h3 class="modal-title">{{$t('shelf.createBookTitle')}}</h3>
      <div class="form-group">
        <label>{{$t('shelf.createBookName')}}</label>
        <input type="text" class="form-input" v-model.trim="bookTitle"
               :placeholder="$t('shelf.createBookNamePlaceholder')" maxlength="50">
      </div>
      <div class="form-group">
        <label>{{$t('shelf.createAuthor')}}</label>
        <input type="text" class="form-input" v-model.trim="bookAuthor"
               :placeholder="$t('shelf.createAuthorPlaceholder')" maxlength="30">
      </div>
      <div class="modal-actions">
        <button class="btn default" @click="$emit('close')">{{$t('button.cancel')}}</button>
        <button class="btn primary" @click="nextStep" :disabled="!bookTitle">
          {{$t('shelf.createNext')}}
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: -2px; margin-left: 4px;"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </template>

    <!--Step 2: 选择内容来源-->
    <template v-if="step === 2">
      <div class="step2-layout">
      <div class="step2-scroll">
      <h3 class="modal-title">{{$t('shelf.selectSourceTitle')}}</h3>
      <p class="modal-desc">{{$t('shelf.selectSourceDesc')}}</p>

      <!--加载中-->
      <div class="source-loading" v-if="loadingSources">
        <div class="loading-icon"></div>
      </div>

      <div class="source-list" v-else>
        <!--Facebook 来源（已授权）-->
        <template v-if="fbSource">
          <div class="source-card">
            <div class="source-card__header">
              <div class="source-card__badge source-card__badge--fb">
                <svg viewBox="0 0 24 24" fill="#fff" width="18" height="18">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <div class="source-card__user">
                <img v-if="fbSource.avatar" :src="fbSource.avatar" class="source-card__avatar"
                     alt="" @error="fbSource.avatar = ''">
                <div>
                  <h4>{{fbSource.name}}</h4>
                  <p class="source-card__date">{{fbSource.dateRange}}</p>
                </div>
              </div>
              <button class="btn-sync" @click.stop="syncFB" :disabled="syncing">
                <template v-if="syncing">
                  <i class="fa fa-spinner fa-spin"></i> {{$t('shelf.fbSyncing')}}
                </template>
                <template v-else>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                  {{$t('shelf.fbSync')}}
                </template>
              </button>
            </div>
            <label class="source-check" :class="{active: selectedSources.includes('facebook')}"
                   @click="toggleSource('facebook')">
              <svg v-if="selectedSources.includes('facebook')" class="source-check__icon" viewBox="0 0 24 24" width="18" height="18" fill="#22c55e"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <svg v-else class="source-check__icon" viewBox="0 0 24 24" width="18" height="18" fill="#d1d5db"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
              <span>{{$t('shelf.fbPostLabel')}} | {{fbSource.postCount}} posts</span>
            </label>
            <label class="source-check" :class="{active: selectedSources.includes('albums')}"
                   @click="toggleSource('albums')" v-if="fbAlbumsCount > 0">
              <svg v-if="selectedSources.includes('albums')" class="source-check__icon" viewBox="0 0 24 24" width="18" height="18" fill="#22c55e"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <svg v-else class="source-check__icon" viewBox="0 0 24 24" width="18" height="18" fill="#d1d5db"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
              <span>{{$t('shelf.fbPhotos')}} | {{fbAlbumsCount}} {{fbAlbumsCount === 1 ? 'album' : 'albums'}}</span>
            </label>
          </div>
        </template>

        <!--Facebook 未授权：只提供 connect 按钮，不提供可勾选项（勾了也没内容可导入）-->
        <a class="source-item source-item--connect" :href="fbAuthLink"
           v-if="!fbSource && !loadingSources">
          <div class="source-item__icon source-item__icon--fb">
            <svg viewBox="0 0 24 24" fill="#fff" width="16" height="16">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          <div class="source-item__info">
            <h4>Facebook</h4>
            <p>{{$t('shelf.fbConnectDesc')}}</p>
          </div>
          <div class="source-item__cta">
            <span class="connect-btn">{{$t('shelf.createConnect') || 'Connect'}}</span>
          </div>
        </a>

        <!--Patch（补写）内容-->
        <div class="source-card" v-if="patchSource">
          <div class="source-card__header">
            <div class="source-card__badge source-card__badge--patch">
              <svg viewBox="0 0 48 48" width="18" height="18"><path fill="#F56368" d="M30.7 19.5c0-2.3-1.8-4.1-4.1-4.1H11.7C5.2 15.4 0 20.7 0 27.2v13.8C0 43.2 1.8 45 4.1 45s4.1-1.8 4.1-4.1V27.2c0-1.9 1.6-3.5 3.5-3.5h14.9c2.3 0 4.1-1.8 4.1-4.2z"/><path fill="#F56368" d="M28.8 0H4.2C1.8 0 0 1.8 0 4.1s1.8 4.1 4.1 4.1h24.5c6.1 0 11.1 5 11.1 11.1s-5 11.1-11.1 11.1h-10c-2.3 0-4.1 1.8-4.1 4.1s1.8 4.1 4.1 4.1h10C39.4 38.6 48 30 48 19.3S39.4 0 28.8 0z"/></svg>
            </div>
            <div class="source-card__user">
              <div>
                <h4>{{ $t('shelf.patchLabel') }}</h4>
                <p class="source-card__date">{{patchSource.dateRange}}</p>
              </div>
            </div>
          </div>
          <label class="source-check" :class="{active: selectedSources.includes('patch')}"
                 @click="toggleSource('patch')">
            <svg v-if="selectedSources.includes('patch')" class="source-check__icon" viewBox="0 0 24 24" width="18" height="18" fill="#22c55e"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            <svg v-else class="source-check__icon" viewBox="0 0 24 24" width="18" height="18" fill="#d1d5db"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
            <span>{{ $t('shelf.patchLabel') }} | {{patchSource.postCount}} posts</span>
          </label>
        </div>

        <!--上传新内容（仅在没有补写内容时显示）-->
        <label class="source-item" :class="{active: selectedSources.includes('upload')}"
               @click="toggleSource('upload')" v-if="!patchSource">
          <div class="source-item__check">
            <svg v-if="selectedSources.includes('upload')" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="currentColor" opacity="0.3"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
          </div>
          <div class="source-item__icon source-item__icon--upload">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" stroke-width="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <div class="source-item__info">
            <h4>{{$t('shelf.sourceUploadNew')}}</h4>
            <p>{{$t('import.tipUpload')}}</p>
          </div>
        </label>
      </div>

      </div><!-- .step2-scroll -->
      <div class="modal-actions step2-actions">
        <button class="btn default" @click="step = 1">{{$t('shelf.createBack')}}</button>
        <button class="btn primary" @click="confirmCreate" :disabled="!selectedSources.length">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" style="vertical-align: -3px; margin-right: 4px;"><path d="M12 5v14M5 12h14"/></svg>
          {{$t('shelf.createConfirm')}}
        </button>
      </div>
      </div><!-- .step2-layout -->
    </template>
  </div>
</template>

<script>
import api from 'src/api/'
import config from 'src/app.config'

export default {
  name: 'create-book-modal',
  props: {
    defaultTitle: { type: String, default: '' },
    defaultAuthor: { type: String, default: '' },
    existingBooks: { type: Array, default: () => [] }
  },
  data () {
    return {
      step: 1,
      bookTitle: this.defaultTitle,
      bookAuthor: this.defaultAuthor,
      selectedSources: [],
      loadingSources: false,
      syncing: false,
      fbSource: null,
      fbAlbumsCount: 0,
      patchSource: null
    }
  },
  computed: {
    fbAuthLink () {
      const link = config.facebookLoginPath
      const { needRedirect } = this.$route.meta
      return needRedirect
        ? link + '?state=redirect_' + encodeURIComponent(window.location.href)
        : link
    }
  },
  watch: {
    defaultTitle (val) { this.bookTitle = val },
    defaultAuthor (val) { this.bookAuthor = val }
  },
  methods: {
    nextStep () {
      if (!this.bookTitle) return
      this.step = 2
      this.fetchSourceInfo()
    },
    async fetchSourceInfo () {
      // 列出本账户下所有书的所有来源（fb/patch/albums），让用户从整个账户
      // 已导入的内容里挑 — 不局限于某一本已存在的书。
      if (!this.existingBooks || !this.existingBooks.length) return

      this.loadingSources = true
      try {
        // 并发拉每本书的 fb/patch/albums，取第一本有内容的作为代表
        const results = await Promise.all(this.existingBooks.map(async (b) => {
          const sid = b.source
          if (!sid) return null
          const [fb, patch, albums] = await Promise.all([
            b.postsInitialized ? api.fetchSocialInfo(sid, 'facebook').catch(() => null) : null,
            b.patched ? api.fetchSocialInfo(sid, 'patch').catch(() => null) : null,
            b.postsInitialized ? api.fetchSocialInfo(sid, 'albums').catch(() => null) : null
          ])
          return { book: b, sid, fb, patch, albums }
        }))
        const rows = results.filter(r => r)

        const sumMonths = (checked, unchecked) => {
          const arr = (checked || []).concat(unchecked || [])
          const total = arr.reduce((s, m) => s + (m.total || 0), 0)
          const months = arr.map(m => m.month).filter(Boolean).sort()
          const range = months.length
            ? `${months[0].substr(0, 4)}/${months[0].substr(4, 2)} ~ ${months[months.length - 1].substr(0, 4)}/${months[months.length - 1].substr(4, 2)}`
            : ''
          return { total, range, hasAny: arr.length > 0 }
        }

        // FB：第一个真有 facebook 数据的 row
        const fbRow = rows.find(r => r.fb && !r.fb.errors && (r.fb.facebook && ((r.fb.facebook.checked || []).length || (r.fb.facebook.unchecked || []).length)))
        if (fbRow) {
          const f = fbRow.fb.facebook
          const { total, range } = sumMonths(f.checked, f.unchecked)
          // FB 身份优先读 user.bindInfo.bind.facebook（X 用户挂 FB 时走这里）
          // 再退 bindInfo.source.facebook（FB 为主 source 的老用户）
          // 最后退 Vuex user —— X 登录时 user 没 fb 头像/昵称，会退成空
          const user = (this.$store && this.$store.getters.user) || {}
          const bind = (user.bindInfo && user.bindInfo.bind) || {}
          const source = (user.bindInfo && user.bindInfo.source) || {}
          const fbInfo = bind.facebook || source.facebook || {}
          this.fbSource = {
            name: fbInfo.username || fbInfo.nickname || fbRow.book.author || 'Facebook',
            avatar: fbInfo.avatar || user.avatar || '',
            postCount: total,
            dateRange: range
          }
          this.selectedSources.push('facebook')
          // Albums piggyback on FB row
          const a = fbRow.albums && fbRow.albums.albums
          if (a) {
            const count = (a.checked || []).length + (a.unchecked || []).length
            if (count > 0) {
              this.fbAlbumsCount = count
              this.selectedSources.push('albums')
            }
          }
        }

        // Patch：第一个真有 patch 数据的 row（0 条不显示）
        const patchRow = rows.find(r => r.patch && !r.patch.errors && (r.patch.patch && ((r.patch.patch.checked || []).length || (r.patch.patch.unchecked || []).length)))
        if (patchRow) {
          const p = patchRow.patch.patch
          const { total, range, hasAny } = sumMonths(p.checked, p.unchecked)
          if (hasAny && total > 0) {
            this.patchSource = { postCount: total, dateRange: range }
            this.selectedSources.push('patch')
          }
        }
      } catch (e) {
        console.error('Failed to fetch source info', e)
      } finally {
        this.loadingSources = false
      }
    },
    toggleSource (source) {
      const idx = this.selectedSources.indexOf(source)
      if (idx >= 0) {
        this.selectedSources.splice(idx, 1)
      } else {
        this.selectedSources.push(source)
      }
    },
    confirmCreate () {
      if (!this.selectedSources.length) return
      this.$emit('confirm', {
        title: this.bookTitle,
        author: this.bookAuthor,
        selectedSources: [...this.selectedSources]
      })
    },
    syncFB () {
      this.$emit('sync-fb')
    },
    reset () {
      this.step = 1
      this.bookTitle = this.defaultTitle
      this.bookAuthor = this.defaultAuthor
      this.selectedSources = []
      this.fbSource = null
      this.fbAlbumsCount = 0
      this.patchSource = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';

.create-book-modal {
  padding: 0.5rem;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.25rem;
  text-align: center;
}

.modal-desc {
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
  margin: -0.5rem 0 1.25rem;
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }

  .form-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 15px;
    color: #1f2937;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;

    &:focus {
      border-color: $el-color-primary;
      box-shadow: 0 0 0 3px rgba($el-color-primary, 0.1);
    }

    &::placeholder {
      color: #d1d5db;
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;

  button {
    min-width: 140px;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 767px) {
    button {
      min-width: 0;
      flex: 1;
    }
    gap: 10px;
  }
}

// Step 2 layout: scrollable sources + fixed bottom actions
.step2-layout {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.step2-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
  max-height: 50vh;

  @media (max-width: 767px) {
    max-height: 55vh;
  }
}

.step2-actions {
  flex-shrink: 0;
  border-top: 1px solid #f3f4f6;
  padding-top: 1rem;
}

.source-loading {
  text-align: center;
  padding: 2rem 0;
}

.source-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

// 已有内容来源卡片（FB / Patch）
.source-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  background: #fff;

  &__header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 10px;

    .btn-sync {
      @media (max-width: 767px) {
        width: 100%;
        justify-content: center;
        margin-top: 6px;
      }
    }
  }

  &__badge {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 10px;

    &--fb {
      background: #1877F2;
    }

    &--patch {
      background: #f3f0ff;
    }

    &--x {
      background: #000;
    }
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;

    h4 {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e5e7eb;
    flex-shrink: 0;
  }

  &__avatar--fb-fallback {
    background: #1877F2;
    border-color: #1877F2;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__date {
    font-size: 12px;
    color: #9ca3af;
    margin: 2px 0 0 !important;
  }
}

.btn-sync {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 12px;
  padding: 4px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  &:hover {
    border-color: $el-color-primary;
    color: $el-color-primary;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// 内容类型勾选项
.source-check {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;

  &__icon {
    flex-shrink: 0;
    margin-right: 10px;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }

  &.active {
    border-color: $el-color-primary;
    background: rgba($el-color-primary, 0.05);
  }

  &:hover {
    border-color: $el-color-primary;
  }
}

// 未授权 / 上传新内容 选项
.source-item {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #d1d5db;
    background: #fafafa;
  }

  &.active {
    border-color: $el-color-primary;
    background: rgba($el-color-primary, 0.04);
  }

  // "Connect" 变体：没有勾选，只是个链接按钮
  &--connect {
    text-decoration: none;
    color: inherit;

    .source-item__cta {
      margin-left: auto;
      flex-shrink: 0;

      .connect-btn {
        display: inline-block;
        padding: 6px 14px;
        border-radius: 999px;
        background: #1877F2;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
      }
    }

    &:hover .connect-btn { opacity: 0.9; }
  }

  &__check {
    flex-shrink: 0;
    margin-right: 10px;
    color: $el-color-primary;
    display: flex;
    align-items: center;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 12px;

    &--fb { background: #1877F2; }
    &--upload { background: $el-color-primary; }
    &--x { background: #000; }
  }

  &__info {
    flex: 1;
    min-width: 0;

    h4 {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }

    p {
      font-size: 12px;
      color: #9ca3af;
      margin: 2px 0 0;
    }
  }
}
</style>
